import React, {useEffect, useState} from 'react';
import {FormControl, InputLabel, MenuItem, Select, Box, Button, Typography} from '@mui/material';
import Popup from '../../../../components/popup';
import {DEMULTIPLEXING_NOTICE, RecallEmail} from '../../../../components/modals/experiments/RecallEmail';
import {step2_form_options} from './formOptions';
import {useNavigate} from 'react-router-dom';
import {useTrovoConfig} from '../../../../utils/config';
import {getBooleanLocalStorageFlag, LOCAL_STORAGE_FLAGS, setBooleanLocalStorageFlag} from '../../../../utils/localStorageFlags';

interface EmailPopupProps {
    sequencing_type: string;
}

const EmailPopup: React.FC<EmailPopupProps> = ({sequencing_type}) => {
    const [dontShowAgain, setDontShowAgain] = useState(() => getBooleanLocalStorageFlag(LOCAL_STORAGE_FLAGS.experimentRecallEmail));
    const [isRecallTemplateModal, setRecallTemplateModal] = useState(
        () => !getBooleanLocalStorageFlag(LOCAL_STORAGE_FLAGS.experimentRecallEmail)
    );

    const handleDontShowAgainChange = (checked: boolean) => {
        setDontShowAgain(checked);
        setBooleanLocalStorageFlag(LOCAL_STORAGE_FLAGS.experimentRecallEmail, checked);
    };

    return (
        <Popup
            isOpen={isRecallTemplateModal}
            onClose={() => setRecallTemplateModal(false)}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description">
            <RecallEmail
                onClose={() => setRecallTemplateModal(false)}
                sequencing_type={sequencing_type}
                dontShowAgain={dontShowAgain}
                onDontShowAgainChange={handleDontShowAgainChange}
            />
        </Popup>
    );
};

/**
 * Turns database representation of sense to a string used in the Step2
 * component, its form options, and as displayed to the user.
 *
 * @param sense 'F', 'R', 'FR', 'RF', or ''
 * @returns The according 'Forward', 'Reverse', or 'Unstranded'. 'Unstranded' is default.
 */
const senseInternalToDisplay = (sense: string) => {
    switch (sense) {
        // singled
        case 'F':
            return 'Forward';
        case 'R':
            return 'Reverse';
        // paired
        case 'FR':
            return 'Forward';
        case 'RF':
            return 'Reverse';
        default:
            return 'Unstranded';
    }
};
/**
 * Turns representation of sense used in Step2 to the one used in
 * CreateExperiment, the database, and the pipelines.
 *
 * @param sense 'Forward', 'Reverse', or 'Unstranded'
 * @param seq_read_type 'paired' or 'singled'
 * @returns 'F', 'R', 'FR', 'RF', or ''
 * @throws Error if invalid sequencing read type or sense
 */
const senseDisplayToInternal = (sense: string, seq_read_type: string) => {
    if (seq_read_type === 'singled') {
        switch (sense) {
            case 'Forward':
                return 'F';
            case 'Reverse':
                return 'R';
            case 'Unstranded':
                return '';
        }
    } else if (seq_read_type === 'paired') {
        switch (sense) {
            case 'Forward':
                return 'FR';
            case 'Reverse':
                return 'RF';
            case 'Unstranded':
                return '';
        }
    }
    throw new Error(`Invalid combo of sequencing read type or sense: '${seq_read_type}' and '${sense}'`);
};

/**
 * Checks if the sequencing details are complete. Completeness means all
 * choices have a value and depends on the sequencing type.
 *
 * @param pageState The current state of the form.
 * @param sequencing_type The sequencing type of the experiment.
 * @return True if the sequencing details are complete for the sequencing_type.
 *         False otherwise or if unknown sequencing_type.
 */
const isCompleteSequencingDetails = (pageState: any, sequencing_type: string) => {
    if (['ChIP Seq', 'ATAC Seq'].includes(sequencing_type)) {
        return Boolean(
            pageState.sequencing_platform && pageState.platform_model && pageState.sequencing_read_type && pageState.files_per_sample
        );
    } else if (sequencing_type === 'RNA Seq') {
        return Boolean(
            pageState.analyzed_molecule &&
                pageState.rna_selection_method &&
                pageState.sequencing_adapter &&
                pageState.sequencing_platform &&
                pageState.platform_model &&
                pageState.sequencing_read_type &&
                pageState.sequencing_sense &&
                pageState.files_per_sample
        );
    } else {
        return false;
    }
};

/**
 * Step2 component for the sequencing details step of the experiment creation form.
 */
const Step2 = ({
    formData,
    setSequencingDetails
}: {
    formData: {experiment_details: ExperimentDetails; sequencing_details: SequencingDetails};
    setSequencingDetails: (obj: SequencingDetails) => void;
}) => {
    const navigate = useNavigate();
    const [pageState, setPageState] = useState<SequencingDetails>({
        analyzed_molecule: formData.sequencing_details.analyzed_molecule || '',
        rna_selection_method: formData.sequencing_details.rna_selection_method || '',
        sequencing_adapter: formData.sequencing_details.sequencing_adapter || '',
        sequencing_platform: formData.sequencing_details.sequencing_platform || '',
        platform_model: formData.sequencing_details.platform_model || '',
        sequencing_read_type: formData.sequencing_details.sequencing_read_type || '',
        // pageState.sequencing_sense is 'Forward', 'Reverse', or 'Unstranded'. The display value.
        sequencing_sense: senseInternalToDisplay(formData.sequencing_details.sequencing_sense) || 'Unstranded',
        files_per_sample: formData.sequencing_details.files_per_sample || ''
    });
    const {user} = useTrovoConfig();

    const isDemoAccount: boolean = user.cb_item_price_id?.includes('demo');

    const complete = isCompleteSequencingDetails(pageState, formData.experiment_details.sequencing_type);

    const handleInputChange = (e: any) => {
        const {name, value} = e.target;
        console.log(`Setting ${name} to ${value}`);
        // Ensure files_per_sample does not get an incorrect state when
        // switching between paired and singled ⇒ clear it
        if (name === 'sequencing_read_type') {
            setPageState({...pageState, [name]: value, files_per_sample: ''});
        } else {
            setPageState({...pageState, [name]: value});
        }
    };

    const handleSubmit = () => {
        // pageState is 'Forward', 'Reverse', or 'Unstranded'
        // Convert to internal representation 'F', 'R', 'FR', 'RF', or ''
        const seqSense = senseDisplayToInternal(pageState.sequencing_sense, pageState.sequencing_read_type);

        setSequencingDetails({
            analyzed_molecule: pageState.analyzed_molecule,
            rna_selection_method: pageState.rna_selection_method,
            sequencing_adapter: pageState.sequencing_adapter,
            sequencing_platform: pageState.sequencing_platform,
            platform_model: pageState.platform_model,
            sequencing_read_type: pageState.sequencing_read_type,
            sequencing_sense: seqSense,
            files_per_sample: pageState.files_per_sample
        });
    };

    const files_per_sample_choices = pageState.sequencing_read_type === 'singled' ? ['1', '2', '3', '4'] : ['2', '4', '6', '8'];

    return (
        <Box component={'form'} sx={{'& label': {width: '60ch'}, display: 'flex', flexDirection: 'column', gap: 4}}>
            <Typography variant="body2">{DEMULTIPLEXING_NOTICE}</Typography>
            {step2_form_options.map(form_option => {
                const {name, readable, select, applies_to} = form_option;
                if (!applies_to.includes(formData.experiment_details.sequencing_type)) {
                    return null;
                }
                let is = pageState[name];

                let options;
                if (name === 'sequencing_read_type') {
                    options =
                        formData.experiment_details.sequencing_type === 'ATAC Seq'
                            ? [
                                  <MenuItem key="paired" value="paired">
                                      Paired-End
                                  </MenuItem>
                              ]
                            : select.map(option => (
                                  <MenuItem key={option} value={option}>
                                      {option === 'singled' ? 'Single-End' : 'Paired-End'}
                                  </MenuItem>
                              ));
                } else {
                    options = (name === 'files_per_sample' ? files_per_sample_choices : select).map(option => (
                        <MenuItem key={option} value={option}>
                            {option}
                        </MenuItem>
                    ));
                }

                return (
                    <FormControl key={name}>
                        <InputLabel>{readable}</InputLabel>
                        <Select
                            name={name}
                            value={is}
                            onChange={handleInputChange}
                            label={readable}
                            disabled={(name === 'files_per_sample' && pageState['sequencing_read_type'] === '') || isDemoAccount}>
                            {options}
                        </Select>
                    </FormControl>
                );
            })}
            <Button variant="contained" color="primary" onClick={handleSubmit} disabled={!complete || isDemoAccount} fullWidth>
                Save and Continue
            </Button>
            <Button variant="text" color="primary" onClick={() => navigate(`/`)} fullWidth>
                Save Progress and Exit
            </Button>
            <EmailPopup sequencing_type={formData.experiment_details.sequencing_type} />
        </Box>
    );
};

export default Step2;
